import produce from 'immer';
import { WritableDraft } from 'immer/dist/types/types-external';
import { RefObject, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, FormSection, formValueSelector, reduxForm } from 'redux-form';
import styled from 'styled-components';

import { TOOLBAR_TRANSITION } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import DisabledFeatureConfigPanel from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel';
import {
  indexForOverride,
  sortDisabledFeatureConfigs,
} from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel/util';
import { produceVtmplVersionWithIdCompareOption } from 'dashboard/apps/VerificationTemplateEditor/forms/SelectSelfieIdCompareOption/utils';
import DataSourceConfigurationPanel from 'dashboard/components/DataSourceConfigurationTable/DataSourceConfigurationPanel';
import MatchRequirementPanel from 'dashboard/components-lib/MatchRequirementPanel';
import { DocumentsSection } from 'dashboard/forms/FormVerificationDocumentTemplateConfig/FormVerificationDocumentTemplateConfig';
import FormVerificationTemplateConfig from 'dashboard/forms/FormVerificationTemplateConfig';
import { VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import {
  MatchRequirementKeys,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import { VendorConfig } from 'dashboard/models/vendor-config';
import {
  isMatchGroupCheck,
  MatchRequirementsCheckConfig,
} from 'dashboard/models/verification-check';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import {
  DisabledFeatureConfig,
  Feature,
  IdCompareOption,
  VerificationTemplateVersion,
  VerificationTemplateVersionDatabase,
} from 'dashboard/models/verification-template-version';
import { RootState } from 'dashboard/redux/store';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

export const VERIFICATION_TEMPLATE_EDITOR_FORM_NAME = 'formVerificationTemplateEditor';

const RootContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
`;

const ControlPanelContainer = styled.div<{ visible: boolean }>`
  background-color: ${({ theme }) => theme.color.background.primary};
  overflow-y: auto;
  overflow-x: hidden;
  transition: flex ${TOOLBAR_TRANSITION};
  border-left: ${({ visible, theme }) => visible && `1px solid ${theme.color.border.primary};`};
  flex: ${({ visible }) => (visible ? '1 0 400px' : '1 0 0px')};
  & > div {
    height: 100%;
  }
`;

export const EDITOR_ROOT_PATH =
  '/dashboard/verification-templates/:verificationTemplateId/:verificationTemplateVersionId';
export const REVIEW_CHANGES_PATH = `${EDITOR_ROOT_PATH}/review-changes`;

export interface VerificationTemplateEditorFormData {
  verificationTemplate: VerificationTemplate;
  verificationTemplateVersion: VerificationTemplateVersion;
  matchRequirementProfiles: MatchRequirementProfile[];
}

interface VerificationTemplateEditorProps {
  editable: boolean;
  isFormPristine: boolean;
  hasInquiryTemplate: boolean;
}

const VerificationTemplateEditor = ({
  editable,
  isFormPristine,
  hasInquiryTemplate,
}: VerificationTemplateEditorProps) => {
  const form = VERIFICATION_TEMPLATE_EDITOR_FORM_NAME;
  const selector = formValueSelector(form);
  const dispatch = useDispatch();
  const verificationTemplateVersion = useSelector<RootState, VerificationTemplateVersion>(
    (state) => selector(state, 'verificationTemplateVersion') || {},
  );
  const matchRequirementProfiles = useSelector<RootState, MatchRequirementProfile[]>(
    (state) => selector(state, 'matchRequirementProfiles') || {},
  );

  const [disabledFeatureConfigToEdit, setDisabledFeatureConfigToEdit] = useState<{
    // Null are only valid initial values.
    'country-code': CountryCode | 'default' | null;
    subdivision: string | 'default' | null;
    'id-classes': GovernmentIdClass[] | 'default' | null;
    'max-age-to-disable'?: number;
    'disabled-features': Feature[];
  } | null>(null);

  const [matchRequirementToEdit, setMatchRequirementToEdit] = useState<MatchRequirementKeys | null>(
    null,
  );

  const [showConfigPanel, { enable: enableConfigsPanel, disable: disableConfigsPanel }] =
    useToggle(false);

  const [configToEdit, setConfigToEdit] = useState<{
    countryCode: string | undefined;
  }>({ countryCode: undefined });

  const [
    showMatchRequirementsPanel,
    { enable: enableMatchRequirementsPanel, disable: disableMatchRequirementsPanel },
  ] = useToggle(false);

  const shouldDisplayConfigsPanel = showConfigPanel && !(configToEdit == null);

  // VerificationTemplateVersionGovernmentId specific state
  const handleCloseDisabledFeatureConfigPanel = () => {
    setDisabledFeatureConfigToEdit(null);
  };

  const handleDisabledFeatureConfigSelected = (
    disabledFeatureConfig: DisabledFeatureConfig | null | undefined,
  ) => {
    if (disabledFeatureConfig == null) {
      handleCloseDisabledFeatureConfigPanel();
    } else {
      handleOpenDisabledFeatureConfigPanel(
        disabledFeatureConfig['country-code'],
        disabledFeatureConfig?.subdivision,
        disabledFeatureConfig['id-classes'],
      );
    }
  };

  const handleOpenDisabledFeatureConfigPanel = (
    countryCode: CountryCode | 'default' | null,
    subdivision: string | 'default' | null,
    idClasses: GovernmentIdClass[] | 'default' | null,
  ) => {
    setDisabledFeatureConfigToEdit({
      'country-code': countryCode,
      subdivision: subdivision,
      'id-classes': idClasses,
      'max-age-to-disable': undefined,
      'disabled-features': [],
    });
  };

  const handleAddDisabledFeatureConfig = (disabledFeatureConfig: DisabledFeatureConfig) => {
    handleUpdateDisabledFeatureConfig(-1, disabledFeatureConfig);
  };

  const handleUpdateDisabledFeatureConfig = (
    index: number,
    disabledFeatureConfig: DisabledFeatureConfig,
  ) => {
    if (verificationTemplateVersion.type !== ApiModel.VerificationTemplateVersionGovernmentId)
      return;

    const updatedVerificationTemplateVersion = produce(verificationTemplateVersion, (draft) => {
      if (index === -1) {
        draft.attributes['external-disabled-feature-configs'].push(disabledFeatureConfig);
      } else {
        draft.attributes['external-disabled-feature-configs'][index] = disabledFeatureConfig;
      }
      // Sort DisabledFeatureConfig to make it easier to parse.
      draft.attributes['external-disabled-feature-configs'] = sortDisabledFeatureConfigs(
        draft.attributes['external-disabled-feature-configs'],
      );
    });
    dispatch(change(form, 'verificationTemplateVersion', updatedVerificationTemplateVersion));
  };

  const handleDeleteDisabledFeatureConfig = () => {
    if (verificationTemplateVersion.type !== ApiModel.VerificationTemplateVersionGovernmentId)
      return;

    const updatedVerificationTemplateVersion = produce(verificationTemplateVersion, (draft) => {
      const disabledFeatureConfigIndex = indexForOverride({
        disabledFeatureConfigs:
          verificationTemplateVersion.attributes['external-disabled-feature-configs'],
        disabledFeatureConfigToFind: disabledFeatureConfigToEdit,
      });
      draft.attributes['external-disabled-feature-configs'].splice(disabledFeatureConfigIndex, 1);
    });

    dispatch(change(form, 'verificationTemplateVersion', updatedVerificationTemplateVersion));
  };

  const handleOpenAddConfigurationPanel = () => {
    setConfigToEdit({ countryCode: undefined });
    enableConfigsPanel();
  };

  const handleConfigToEdit = (countryCode: string) => {
    setConfigToEdit({ countryCode });
    enableConfigsPanel();
  };

  const handleCloseConfigsPanel = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setConfigToEdit(null);
    disableConfigsPanel();
  };

  const handleUpdateConfig = (configurations: VendorConfig[], countryCode: string) => {
    const updatedVtmplVersion = produce(
      verificationTemplateVersion,
      (draftVtmplVersion: WritableDraft<VerificationTemplateVersionDatabase>) => {
        if (draftVtmplVersion.attributes && draftVtmplVersion.attributes['vendor-configurations']) {
          draftVtmplVersion.attributes['vendor-configurations'][countryCode] = configurations;
        }
      },
    );
    dispatch(change(form, 'verificationTemplateVersion', updatedVtmplVersion));
    return;
  };

  const handleDeleteConfig = (countryCode: string) => {
    const updatedVtmplVersion = produce(
      verificationTemplateVersion,
      (draftVtmplVersion: WritableDraft<VerificationTemplateVersionDatabase>) => {
        if (draftVtmplVersion.attributes && draftVtmplVersion.attributes['vendor-configurations']) {
          delete draftVtmplVersion.attributes['vendor-configurations'][countryCode];
        }
      },
    );
    dispatch(change(form, 'verificationTemplateVersion', updatedVtmplVersion));
    return;
  };

  const dataSourceConfigModal = useMemo(() => {
    if (
      (verificationTemplateVersion as VerificationTemplateVersionDatabase)?.attributes?.[
        'vendor-configurations'
      ] == null
    )
      return null;
    return (
      <DataSourceConfigurationPanel
        vtmplVersion={verificationTemplateVersion as VerificationTemplateVersionDatabase}
        selectedCountry={configToEdit?.countryCode}
        closeConfig={handleCloseConfigsPanel}
        updateConfig={handleUpdateConfig}
        deleteConfig={handleDeleteConfig}
      />
    );
  }, [
    configToEdit?.countryCode,
    handleCloseConfigsPanel,
    handleDeleteConfig,
    handleUpdateConfig,
    verificationTemplateVersion,
  ]);

  const handleUpdateIdCompareOption = (idCompareOption: IdCompareOption) => {
    const draftVtmplVersion = produceVtmplVersionWithIdCompareOption(
      verificationTemplateVersion,
      idCompareOption,
    );
    if (draftVtmplVersion) {
      dispatch(change(form, 'verificationTemplateVersion', draftVtmplVersion));
    }
  };
  const documentVerificationSectionRefs = useRef<
    Record<DocumentsSection, RefObject<HTMLDivElement>>
  >({
    [DocumentsSection.General]: { current: null },
    [DocumentsSection.Classifications]: { current: null },
    [DocumentsSection.Extractions]: { current: null },
    [DocumentsSection.Insights]: { current: null },
    [DocumentsSection.DocumentFields]: { current: null },
    [DocumentsSection.VerificationChecks]: { current: null },
    [DocumentsSection.DocumentTester]: { current: null },
  });

  return (
    <RootContainer>
      <ContentContainer data-test='verification-templates-show-view'>
        <FormVerificationTemplateConfig
          vtmplVersion={verificationTemplateVersion}
          linkedStepsElement={null}
          matchRequirementProfiles={matchRequirementProfiles}
          selectedMatchRequirement={null}
          // only allows editing if the user is a personerd
          hasEditPermission={editable}
          formSectionName='verificationTemplateVersion'
          handleMatchRequirementSelected={(matchRequirementKeys: MatchRequirementKeys) => {
            setMatchRequirementToEdit(matchRequirementKeys);
            enableMatchRequirementsPanel();
          }}
          handleOpenAddConfigPanel={handleOpenAddConfigurationPanel}
          handleConfigSelected={handleConfigToEdit}
          selectedCountryCode={configToEdit?.countryCode}
          selectedDisabledFeatureConfig={disabledFeatureConfigToEdit}
          handleDisabledFeatureConfigSelected={handleDisabledFeatureConfigSelected}
          handleUpdateIdCompareOption={handleUpdateIdCompareOption}
          handleUpdateVerificationTemplateVersion={(updatedVerificationTemplateVersion) =>
            dispatch(
              change(form, 'verificationTemplateVersion', updatedVerificationTemplateVersion),
            )
          }
          isFormPristine={isFormPristine}
          hasInquiryTemplate={hasInquiryTemplate}
          documentVerificationSectionRefs={documentVerificationSectionRefs}
          title={`${VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[verificationTemplateVersion.type]} Verification`}
        />
      </ContentContainer>
      <ControlPanelContainer
        visible={showMatchRequirementsPanel}
        id='match-requirement-panel__container'
      >
        <FormSection name='verificationTemplateVersion'>
          {showMatchRequirementsPanel && matchRequirementToEdit && (
            <MatchRequirementPanelWrapper
              verificationTemplateVersion={verificationTemplateVersion}
              matchRequirementProfiles={matchRequirementProfiles}
              matchRequirementKeys={matchRequirementToEdit}
              editable={editable}
              handleClose={() => {
                setMatchRequirementToEdit(null);
                disableMatchRequirementsPanel();
              }}
            />
          )}
        </FormSection>
      </ControlPanelContainer>
      <ControlPanelContainer
        visible={
          useFeatureFlag('dashboard:enable-disabled-feature-configs') &&
          disabledFeatureConfigToEdit != null
        }
        id='disabled-feature-config-panel__container'
      >
        <FormSection name='verificationTemplateVersion'>
          {disabledFeatureConfigToEdit &&
            verificationTemplateVersion.type ===
              ApiModel.VerificationTemplateVersionGovernmentId && (
              <DisabledFeatureConfigPanel
                key={`${disabledFeatureConfigToEdit['country-code']}_${disabledFeatureConfigToEdit.subdivision}`}
                vtmplVersion={verificationTemplateVersion}
                disabledFeatureConfigToEdit={disabledFeatureConfigToEdit}
                closeDisabledFeatureConfig={handleCloseDisabledFeatureConfigPanel}
                addDisabledFeatureConfig={handleAddDisabledFeatureConfig}
                updateDisabledFeatureConfig={handleUpdateDisabledFeatureConfig}
                deleteDisabledFeatureConfig={handleDeleteDisabledFeatureConfig}
              />
            )}
        </FormSection>
      </ControlPanelContainer>
      <ControlPanelContainer visible={shouldDisplayConfigsPanel}>
        {dataSourceConfigModal}
      </ControlPanelContainer>
    </RootContainer>
  );
};

interface MatchRequirementPanelWrapperProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  matchRequirementProfiles: MatchRequirementProfile[];
  matchRequirementKeys: MatchRequirementKeys;
  editable: boolean;
  handleClose: () => void;
}

const MatchRequirementPanelWrapper = ({
  verificationTemplateVersion,
  matchRequirementProfiles,
  matchRequirementKeys,
  editable,
  handleClose,
}: MatchRequirementPanelWrapperProps) => {
  const {
    'match-group-index': matchGroupIndex,
    'match-requirement-index': matchRequirementIndex,
    scope: checkName,
  } = matchRequirementKeys;

  // Guard from long loads resulting in null verificationTemplateVersion screens of death
  if (verificationTemplateVersion == null) {
    return null;
  }

  const verificationCheckIndex = verificationTemplateVersion.attributes[
    'verification-checks'
  ].findIndex((check) => check.check === checkName);
  const verificationCheck = verificationTemplateVersion.attributes['verification-checks'][
    verificationCheckIndex
  ] as MatchRequirementsCheckConfig;
  const usesMatchGroups = isMatchGroupCheck(verificationCheck);

  const matchRequirement =
    usesMatchGroups &&
    verificationCheck['match-groups'] &&
    verificationCheck['match-groups'][matchGroupIndex]
      ? verificationCheck['match-groups'][matchGroupIndex]['match-requirements'][
          matchRequirementIndex
        ]
      : verificationCheck['check-requirements']?.[matchRequirementIndex];
  const matchRequirementName = usesMatchGroups
    ? `match-groups.${matchGroupIndex}.match-requirements.${matchRequirementIndex}`
    : `check-requirements.${matchRequirementIndex}`;
  const formSectionName = `attributes.verification-checks.${verificationCheckIndex}`;

  if (!matchRequirement) return null;
  return (
    <MatchRequirementPanel
      usesMatchGroups={usesMatchGroups}
      matchRequirementProfiles={matchRequirementProfiles}
      matchRequirement={matchRequirement}
      matchRequirementName={matchRequirementName}
      matchRequirementKeys={matchRequirementKeys}
      formSectionName={formSectionName}
      editable={editable}
      handleClose={handleClose}
    />
  );
};

export default reduxForm<VerificationTemplateEditorFormData, VerificationTemplateEditorProps>({
  form: VERIFICATION_TEMPLATE_EDITOR_FORM_NAME,
  enableReinitialize: true,
})(VerificationTemplateEditor);
